import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Calendar, Clock, Plus, ChevronLeft, ChevronRight, Video,
  Users, Brain, Filter, Search, MoreVertical, X, Check
} from 'lucide-react';

const BehavioralHealthSchedule = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [showNewAppointment, setShowNewAppointment] = useState(false);

  const appointments = [
    { id: 1, client: 'Sarah Johnson', time: '9:00 AM', duration: 60, type: 'Individual Therapy', status: 'confirmed', day: 1 },
    { id: 2, client: 'Michael Chen', time: '10:30 AM', duration: 90, type: 'Couples Counseling', status: 'confirmed', day: 1 },
    { id: 3, client: 'Emily Rodriguez', time: '1:00 PM', duration: 60, type: 'CBT Session', status: 'pending', day: 1 },
    { id: 4, client: 'David Thompson', time: '3:00 PM', duration: 60, type: 'Initial Assessment', status: 'confirmed', day: 1 },
    { id: 5, client: 'Jennifer Williams', time: '10:00 AM', duration: 60, type: 'Follow-up', status: 'confirmed', day: 2 },
    { id: 6, client: 'Robert Garcia', time: '2:00 PM', duration: 60, type: 'Group Therapy', status: 'confirmed', day: 3 },
    { id: 7, client: 'Lisa Anderson', time: '11:00 AM', duration: 45, type: 'Check-in', status: 'confirmed', day: 4 },
    { id: 8, client: 'James Wilson', time: '9:30 AM', duration: 60, type: 'Individual Therapy', status: 'pending', day: 5 },
  ];

  const hours = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const getAppointmentStyle = (status) => {
    switch(status) {
      case 'confirmed': return 'bg-green-100 border-green-500 text-green-800';
      case 'pending': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'cancelled': return 'bg-red-100 border-red-500 text-red-800';
      default: return 'bg-gray-100 border-gray-500 text-gray-800';
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-orange-500" />
                Session Schedule
              </h1>
              <p className="text-gray-500 mt-1">Manage your therapy appointments</p>
            </div>
            <button 
              onClick={() => setShowNewAppointment(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Appointment
            </button>
          </div>
        </div>

        {/* Calendar Controls */}
        <div className="bg-white rounded-xl shadow mb-6">
          <div className="p-4 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h2 className="text-xl font-bold text-gray-900">February 24 - 28, 2026</h2>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setViewMode('day')}
                className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'day' ? 'bg-orange-100 text-orange-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Day
              </button>
              <button 
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'week' ? 'bg-orange-100 text-orange-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Week
              </button>
              <button 
                onClick={() => setViewMode('month')}
                className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'month' ? 'bg-orange-100 text-orange-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Month
              </button>
            </div>
          </div>

          {/* Week View */}
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Days Header */}
              <div className="grid grid-cols-6 border-b border-gray-200">
                <div className="p-4 text-center text-gray-500 text-sm font-medium border-r border-gray-200">
                  Time
                </div>
                {days.map((day, idx) => (
                  <div key={day} className={`p-4 text-center border-r border-gray-200 ${idx === 0 ? 'bg-orange-50' : ''}`}>
                    <p className="font-semibold text-gray-900">{day}</p>
                    <p className="text-sm text-gray-500">Feb {24 + idx}</p>
                  </div>
                ))}
              </div>

              {/* Time Slots */}
              {hours.map((hour) => (
                <div key={hour} className="grid grid-cols-6 border-b border-gray-100">
                  <div className="p-2 text-center text-gray-500 text-sm border-r border-gray-200 bg-gray-50">
                    {hour}
                  </div>
                  {days.map((day, dayIdx) => {
                    const dayAppointments = appointments.filter(apt => apt.day === dayIdx + 1 && apt.time === hour);
                    return (
                      <div key={`${day}-${hour}`} className="p-1 min-h-[80px] border-r border-gray-100 hover:bg-gray-50 transition-colors">
                        {dayAppointments.map((apt) => (
                          <div 
                            key={apt.id}
                            className={`p-2 rounded-lg border-l-4 mb-1 cursor-pointer hover:shadow transition-shadow ${getAppointmentStyle(apt.status)}`}
                          >
                            <p className="font-medium text-sm truncate">{apt.client}</p>
                            <p className="text-xs opacity-75">{apt.type}</p>
                            <p className="text-xs opacity-75">{apt.duration} min</p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upcoming Sessions List */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Today's Sessions</h3>
          </div>
          <div className="p-4 space-y-3">
            {appointments.filter(apt => apt.day === 1).map((apt) => (
              <div key={apt.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                    {apt.client.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{apt.client}</h4>
                    <p className="text-sm text-gray-500">{apt.type} • {apt.duration} min</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{apt.time}</p>
                    <span className={`text-xs px-2 py-1 rounded ${
                      apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {apt.status}
                    </span>
                  </div>
                  <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Start
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* New Appointment Modal */}
        {showNewAppointment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Schedule New Session</h3>
                <button onClick={() => setShowNewAppointment(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                  <select className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option>Select a client...</option>
                    <option>Sarah Johnson</option>
                    <option>Michael Chen</option>
                    <option>Emily Rodriguez</option>
                    <option>David Thompson</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input type="date" className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input type="time" className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session Type</label>
                  <select className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option>Individual Therapy</option>
                    <option>Couples Counseling</option>
                    <option>Group Therapy</option>
                    <option>Initial Assessment</option>
                    <option>Follow-up</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <select className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option>30 minutes</option>
                    <option>45 minutes</option>
                    <option>60 minutes</option>
                    <option>90 minutes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea 
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows={3}
                    placeholder="Add any notes for this session..."
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button 
                  onClick={() => setShowNewAppointment(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                  Schedule Session
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BehavioralHealthSchedule;
